import { Redo2, Undo2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { TooltipProvider, WithTooltip } from '@/components/ui/tooltip'
import { cn } from '@/components/ui/utils'
import { describeCombo } from '@/lib/keys'
import { useEditorStore, type EditorState } from '@/stores/editorStore'

import { runRedo, runUndo } from './useEditorCommands'

/**
 * Undo / Redo buttons for either editor shell; they do exactly what Ctrl+Z / Ctrl+Y do (while a
 * shape is half drawn, Undo drops its last dot). Disabled while there is nothing to undo or redo,
 * and while a drag is in progress. Needs no props.
 */

const drawing = (s: EditorState) => s.toolState.t === 'polygon.drawing' || s.toolState.t === 'path.drawing'
const selectUndoable = (s: EditorState) => !s.gesturing && (s.history.past.length > 0 || drawing(s))
const selectRedoable = (s: EditorState) => !s.gesturing && !drawing(s) && s.history.future.length > 0

export interface UndoRedoButtonsProps {
  className?: string
}

export function UndoRedoButtons({ className }: UndoRedoButtonsProps = {}) {
  const hasDoc = useEditorStore((s) => s.doc !== null)
  const canUndo = useEditorStore(selectUndoable)
  const canRedo = useEditorStore(selectRedoable)
  if (!hasDoc) return null
  return (
    <TooltipProvider delayDuration={300}>
      <div role="group" aria-label="Undo and redo" className={cn('flex items-center gap-0.5', className)}>
        <WithTooltip label={`Undo (${describeCombo('$mod+z')})`}>
          <Button variant="ghost" size="sm" className="w-9 px-0" aria-label="Undo" disabled={!canUndo} onClick={() => runUndo()}>
            <Undo2 aria-hidden />
          </Button>
        </WithTooltip>
        <WithTooltip label={`Redo (${describeCombo('$mod+y')})`}>
          <Button variant="ghost" size="sm" className="w-9 px-0" aria-label="Redo" disabled={!canRedo} onClick={() => runRedo()}>
            <Redo2 aria-hidden />
          </Button>
        </WithTooltip>
      </div>
    </TooltipProvider>
  )
}
